import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Clock, Users, PartyPopper } from "lucide-react";
import { API } from "@/App";
import { useLanguage } from "@/LanguageContext";
import { toast } from "sonner";

const BookingPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    booking_date: "",
    booking_time: "",
    number_of_guests: 2,
    special_requests: "",
    marketing_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [specialLabel, setSpecialLabel] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);

  const fetchSlots = useCallback(async (date) => {
    if (!date) { setAvailableSlots([]); setSpecialLabel(""); return; }
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API}/booking/available-times?date=${date}`);
      setAvailableSlots(res.data.slots || []);
      setSpecialLabel(res.data.label || "");
    } catch {
      setAvailableSlots([]);
      setSpecialLabel("");
    }
    setLoadingSlots(false);
  }, []);

  useEffect(() => {
    if (formData.booking_date) fetchSlots(formData.booking_date);
  }, [formData.booking_date, fetchSlots]);

  const handleDateChange = (date) => {
    setFormData({ ...formData, booking_date: date, booking_time: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/bookings`, formData);
      toast.success("Reservasjonsforespørsel sendt! Vi bekrefter snart via e-post.");
      setFormData({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        booking_date: "",
        booking_time: "",
        number_of_guests: 2,
        special_requests: "",
        marketing_consent: false,
      });
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Kunne ikke sende reservasjon. Vennligst prøv igjen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Header */}
      <div 
        className="py-16 mandala-pattern"
        style={{ 
          background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="indian-divider mb-4"
          >
            <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>Reservasjon</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-serif-display font-semibold italic mb-4"
            style={{ color: '#d4af37' }}
            data-testid="booking-page-title"
          >
            {t("booking_title")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ color: '#f5e6d3' }}
          >
            {t("booking_subtitle")}
          </motion.p>
        </div>
      </div>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-8"
          style={{ border: '2px solid #d4af37' }}
          data-testid="booking-form-container"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="customer_name" style={{ color: '#2d1810' }}>{t("booking_name")} *</Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                  className="indian-input mt-1"
                  data-testid="booking-name-input"
                />
              </div>
              <div>
                <Label htmlFor="customer_email" style={{ color: '#2d1810' }}>E-post *</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  required
                  className="indian-input mt-1"
                  data-testid="booking-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="customer_phone" style={{ color: '#2d1810' }}>{t("booking_phone")} *</Label>
              <Input
                id="customer_phone"
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                required
                className="indian-input mt-1"
                data-testid="booking-phone-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label htmlFor="booking_date" className="flex items-center gap-2" style={{ color: '#2d1810' }}>
                  <Calendar className="h-4 w-4" style={{ color: '#721111' }} />
                  Dato *
                </Label>
                <Input
                  id="booking_date"
                  type="date"
                  value={formData.booking_date}
                  onChange={(e) => handleDateChange(e.target.value)}
                  required
                  min={new Date().toISOString().split("T")[0]}
                  className="indian-input mt-1"
                  data-testid="booking-date-input"
                />
                {specialLabel && (
                  <div className="flex items-center gap-1 mt-1 px-2 py-1 rounded-full text-xs font-semibold" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }}>
                    <PartyPopper className="h-3 w-3" /> {specialLabel}
                  </div>
                )}
              </div>
              <div className="md:col-span-2">
                <Label className="flex items-center gap-2" style={{ color: '#2d1810' }}>
                  <Clock className="h-4 w-4" style={{ color: '#721111' }} />
                  Velg tid *
                </Label>
                {!formData.booking_date ? (
                  <p className="text-sm mt-2" style={{ color: '#8b4513' }}>Velg dato forst for a se ledige tider</p>
                ) : loadingSlots ? (
                  <p className="text-sm mt-2" style={{ color: '#8b4513' }}>Laster tider...</p>
                ) : availableSlots.length === 0 ? (
                  <p className="text-sm mt-2" style={{ color: '#dc2626' }}>Ingen ledige tider for denne datoen</p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-2">
                    {availableSlots.map(slot => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setFormData({ ...formData, booking_time: slot })}
                        className="px-3 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105"
                        style={{
                          background: formData.booking_time === slot ? '#721111' : '#f4e7d8',
                          color: formData.booking_time === slot ? '#d4af37' : '#2d1810',
                          border: formData.booking_time === slot ? '2px solid #d4af37' : '1px solid #d4af37'
                        }}
                        data-testid={`booking-time-${slot}`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="number_of_guests" className="flex items-center gap-2" style={{ color: '#2d1810' }}>
                  <Users className="h-4 w-4" style={{ color: '#721111' }} />
                  Gjester *
                </Label>
                <Input
                  id="number_of_guests"
                  type="number"
                  min="1"
                  max="20"
                  value={formData.number_of_guests}
                  onChange={(e) =>
                    setFormData({ ...formData, number_of_guests: parseInt(e.target.value) })
                  }
                  required
                  className="indian-input mt-1"
                  data-testid="booking-guests-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="special_requests" style={{ color: '#2d1810' }}>{t("booking_requests")}</Label>
              <Textarea
                id="special_requests"
                value={formData.special_requests}
                onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                placeholder="Allergier, diettrestriksjoner, feiring, etc."
                rows={4}
                className="indian-input mt-1"
                data-testid="booking-requests-textarea"
              />
            </div>

            <div className="p-4 rounded-xl mt-2 mb-4" style={{ background: '#fdf8f0', border: '1px solid #d4af37' }}>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="marketing-consent-checkbox">
                <input
                  type="checkbox"
                  checked={formData.marketing_consent === true}
                  onChange={(e) => setFormData({...formData, marketing_consent: e.target.checked})}
                  className="w-5 h-5 rounded accent-green-700"
                />
                <span className="font-semibold text-sm" style={{ color: '#2d1810' }}>
                  Ja, jeg vil gjerne motta tilbud og rabatter pa e-post
                </span>
              </label>
              <p className="text-sm mt-2 ml-8" style={{ color: '#8b7355' }}>
                Du kan melde deg av nar som helst
              </p>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full btn-maroon rounded-full font-bold py-6 text-lg transition-transform hover:scale-105 active:scale-95"
              data-testid="booking-submit-button"
            >
              {submitting ? t("booking_submitting") : t("booking_submit")}
            </Button>
          </form>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default BookingPage;
